import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { CalendarDays, Search, Inbox, User, Bell, LogOut, Music2, ShieldCheck, Users, Megaphone, Home as HomeIcon, AlertTriangle, MessageSquare, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useEffect, useState } from "react";
import api from "../lib/api";
import FeedbackModal from "./FeedbackModal";
import WhatsNewModal from "./WhatsNewModal";
import MigrationConfirmationModal from "./MigrationConfirmationModal";

const navMusician = [
  { to: "/home", label: "Home", icon: HomeIcon, testid: "nav-home" },
  { to: "/diary", label: "Diary", icon: CalendarDays, testid: "nav-diary" },
  { to: "/search", label: "Find", icon: Search, testid: "nav-search" },
  { to: "/deps", label: "Deps", icon: Megaphone, testid: "nav-deps" },
  { to: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

const navBooker = [
  { to: "/home", label: "Home", icon: HomeIcon, testid: "nav-home" },
  { to: "/search", label: "Find", icon: Search, testid: "nav-search" },
  { to: "/deps", label: "Deps", icon: Megaphone, testid: "nav-deps" },
  { to: "/bookings", label: "Bookings", icon: Inbox, testid: "nav-bookings" },
  { to: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(() => {
    try { return Number(window.localStorage.getItem("amds_unread_cache") || 0); } catch { return 0; }
  });
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Force users with `must_change_password` to the password card before
  // anything else. Admin-set temp passwords / force-change flag both land here.
  useEffect(() => {
    if (!user || user === false) return;
    if (user.must_change_password && !location.pathname.startsWith("/profile")) {
      navigate("/profile#password", { replace: true });
    }
  }, [user, location.pathname, navigate]);

  useEffect(() => {
    if (!user || user === false) return undefined;
    let mounted = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (!mounted) return;
        const n = (data || []).filter((n) => !n.read).length;
        setUnread(n);
        try { window.localStorage.setItem("amds_unread_cache", String(n)); } catch { /* ignore storage errors */ }
      } catch { /* ignore polling errors — retry on next tick */ }
    };
    // Defer the first poll to the next paint so Safari renders the chrome
    // before chasing /api/notifications. Saves ~300-500ms on first show.
    const startId = setTimeout(tick, 250);
    const id = setInterval(tick, 30000);
    return () => { mounted = false; clearTimeout(startId); clearInterval(id); };
  }, [user]);

  // Admins get the musician nav by default — admin is a musician-plus role.
  // Occasional admin functions remain accessible via the shield icon in the
  // header (see below). This keeps the daily musician workflow primary.
  const items = (user?.role === "musician" || user?.role === "admin") ? navMusician : navBooker;

  return (
    <div className="min-h-screen bg-ivory text-ink-900">
      {/* Top bar */}
      <header className="glass-bar sticky top-0 z-40 border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <button
            onClick={() => navigate("/home")}
            className="flex items-center gap-2"
            data-testid="header-logo-btn"
          >
            <span className="w-8 h-8 rounded-xl bg-sage-500 text-white grid place-items-center">
              <Music2 size={18} strokeWidth={2.2} />
            </span>
            <span className="font-heading font-semibold text-ink-900 tracking-tight">AMDS</span>
          </button>
          <div className="flex items-center gap-1">
            {user?.role === "admin" && (
              <button
                onClick={() => navigate("/admin")}
                className="btn-ghost text-sm"
                data-testid="header-admin-btn"
                title="Admin"
              >
                <ShieldCheck size={18} />
              </button>
            )}
            <button
              onClick={() => navigate("/notifications")}
              className="relative btn-ghost"
              data-testid="header-bell-btn"
              aria-label="Notifications"
            >
              <Bell size={20} />
              {unread > 0 && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-terracotta-500" data-testid="unread-dot" />
              )}
            </button>
            <button onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost" data-testid="header-logout-btn" aria-label="Log out">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {user?.must_change_password && (
        <div className="bg-terracotta-50 border-b border-terracotta-200" data-testid="must-change-password-banner">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-2 text-sm text-terracotta-800">
            <AlertTriangle size={14} className="shrink-0" />
            <span>You need to set a new password before continuing. Use the form below.</span>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-4 pb-44 sm:pb-12 animate-fade-up">{children}</main>

      {/* Sitewide footer — beta trust links */}
      <footer className="max-w-5xl mx-auto px-4 sm:px-6 pb-24 sm:pb-6 mt-6 border-t border-ivory-200 pt-5" data-testid="site-footer">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink-500 justify-center sm:justify-start">
          <Link to="/about" className="hover:text-sage-600" data-testid="footer-about">About AMDS</Link>
          <Link to="/privacy-security" className="hover:text-sage-600" data-testid="footer-privacy-security">Privacy &amp; Security</Link>
          <Link to="/privacy-policy" className="hover:text-sage-600" data-testid="footer-privacy-policy">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-sage-600" data-testid="footer-terms">Terms</Link>
          <Link to="/whats-new" className="hover:text-sage-600 inline-flex items-center gap-1" data-testid="footer-whats-new">
            <Sparkles size={12} /> What's new
            {user && user.release_notes && user.release_notes.has_unseen && (
              <span
                className="w-1.5 h-1.5 rounded-full bg-sage-500"
                aria-label="New release notes available"
                data-testid="whats-new-nav-dot"
              />
            )}
          </Link>
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="inline-flex items-center gap-1 hover:text-sage-600"
            data-testid="footer-feedback-btn"
          >
            <MessageSquare size={12} /> Found a bug or have a suggestion?
          </button>
        </div>
        <div className="text-[11px] text-ink-400 mt-2 text-center sm:text-left">
          AMDS — public beta · © {new Date().getFullYear()}
        </div>
      </footer>

      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} currentUser={user || null} />
      <WhatsNewModal />
      <MigrationConfirmationModal />

      {/* Bottom nav (mobile) — lifted above the platform "Made with Emergent" badge */}
      <nav
        className="glass-bar fixed left-0 right-0 z-50 border-t pb-safe sm:hidden bottom-[64px]"
        data-testid="bottom-nav"
      >
        <div className="grid grid-cols-5">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${isActive ? "text-sage-600" : "text-ink-500 hover:text-sage-500"}`
              }
            >
              {({ isActive }) => (
                <>
                  <it.icon size={20} strokeWidth={isActive ? 2.4 : 2} />
                  <span>{it.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Tabs (desktop) */}
      <nav className="hidden sm:flex justify-center -mt-2 mb-6 sticky top-14 z-30">
        <div className="flex gap-1 bg-white/70 backdrop-blur-xl border border-ivory-200 rounded-full p-1 shadow-soft">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`desk-${it.testid}`}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-all ${isActive ? "bg-sage-500 text-white" : "text-ink-700 hover:bg-ivory-100"}`
              }
            >
              <it.icon size={16} />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
